import { useTranslation } from 'react-i18next';

/**
 * The stage a payment is at, from the payer's point of view rather than the protocol's.
 *
 * `preparing`  — the start request is in flight; nothing has been sent to a bank yet.
 * `redirecting`— the provider accepted and the browser is on its way to the bank's page.
 * `processing` — the provider's own form is mounting, or a submitted payment is being taken.
 * `verifying`  — the payer is back and Barah Pay is asking the provider what actually happened.
 */
export type PaymentPhase = 'preparing' | 'redirecting' | 'processing' | 'verifying';

const TITLE_KEY: Record<PaymentPhase, string> = {
  preparing: 'checkout.preparingPayment',
  redirecting: 'checkout.redirecting',
  processing: 'checkout.processingPayment',
  verifying: 'checkout.verifyingPayment',
};

/** The three dots are ordered by phase so the payer can see how far along the payment is. */
const STEP_ORDER: PaymentPhase[] = ['preparing', 'redirecting', 'verifying'];

const STEP_OF: Record<PaymentPhase, number> = {
  preparing: 0,
  redirecting: 1,
  // A provider form mounting on our own page replaces the redirect, so it occupies the same step.
  processing: 1,
  verifying: 2,
};

/**
 * The waiting state of the payment surface (spec section 18).
 *
 * A payment has dead time in it that a page cannot remove: a provider call, a redirect, a status
 * inquiry after the payer returns. Left unlabelled that time reads as a broken page, and a payer who
 * believes the page has broken reloads it or presses the button again — which is how one payment
 * becomes two. Naming the stage is what stops that, so this is a payment control as much as a
 * presentational one.
 *
 * `aria-live="polite"` announces each change once, so the same information reaches a screen reader
 * without interrupting whatever it is already reading.
 */
export function PaymentProgress({
  phase,
  slow = false,
}: {
  phase: PaymentPhase;
  /** Set once the wait has run past what is normal, to explain the delay rather than leave it silent. */
  slow?: boolean;
}) {
  const { t } = useTranslation();
  const current = STEP_OF[phase];

  return (
    <div className="bp-progress" role="status" aria-live="polite">
      <div className="bp-spinner" aria-hidden="true" />

      <div className="bp-progress__title">{t(TITLE_KEY[phase])}</div>

      {/*
        The payer must not close the tab mid-flight: on a redirect the payment is already with the
        bank, and while verifying the outcome is known to the provider but not yet shown here.
      */}
      <div className="bp-progress__hint">{t('checkout.doNotClose')}</div>

      {slow ? <div className="bp-progress__hint bp-progress__hint--slow">{t('checkout.stillVerifying')}</div> : null}

      <ol className="bp-progress__steps" aria-hidden="true">
        {STEP_ORDER.map((step, index) => (
          <li
            key={step}
            className={
              'bp-progress__step'
              + (index < current ? ' bp-progress__step--done' : '')
              + (index === current ? ' bp-progress__step--active' : '')
            }
          />
        ))}
      </ol>
    </div>
  );
}
